import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import useAdaptivePolling from "@/hooks/useAdaptivePolling";
import useTimerWS from "@/hooks/useTimerWS";
import useNetworkStatus from "@/hooks/useNetworkStatus";
import { safeConfirm } from "@/utils/safeConfirm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Play, Eye, Trophy, ArrowRight, Award, Edit, Settings, RefreshCw, RotateCcw, CheckCircle, Circle, Upload, Download, Users, UserCheck, UserX, PlayCircle, Globe, ArrowLeft, ExternalLink, Copy, Link, SkipForward, QrCode, WifiOff } from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import { CountrySelect } from "@/components/CountrySelect";
import CircleManager from "@/components/CircleManager";
import { ControlTab } from "@/components/director/ControlTab";
import { AthletesTab } from "@/components/director/AthletesTab";
import { JudgesTab } from "@/components/director/JudgesTab";
import { TournamentTab } from "@/components/director/TournamentTab";
import { ThemeToggle } from "@/components/ThemeToggle";
import { SettingsTab } from "@/components/director/SettingsTab";
import { useEvent } from "@/components/EventLayout";
import useSimulationMode from "@/hooks/useSimulationMode";
import useKeyboardShortcuts from "@/hooks/useKeyboardShortcuts";
import useComms from "@/hooks/useComms";
import { QRCodeSVG } from "qrcode.react";
import PhotoCropDialog from "@/components/PhotoCropDialog";
import PhotoPickerButtons from "@/components/PhotoPickerButtons";
import { compressImage } from "@/utils/compressImage";

import { BACKEND_URL } from '../utils/apiBase';
const API = `${BACKEND_URL}/api`;

// Helper function to build correct image URL (handles both Cloudinary and local paths)
const getImageUrl = (url) => {
  if (!url) return '';
  // If it's already a full Cloudinary URL, return as-is
  if (url.startsWith('https://res.cloudinary.com/') || url.startsWith('http://res.cloudinary.com/')) {
    return url;
  }
  // If it's a relative path, prepend BACKEND_URL
  if (url.startsWith('/api/uploads/')) {
    return `${BACKEND_URL}${url}`;
  }
  // Default: treat as relative path
  return `${BACKEND_URL}${url}`;
};

const DirectorPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const event = useEvent();
  const { theme, updateTheme, resetTheme } = useTheme();
  const { t, lang, setLang, availableLanguages } = useI18n();
  const { battleTimers, circleTimers, settings: countdownSettings, refreshBattle: refreshBattleTimer, refreshCircle: refreshCircleTimer } = useTimerWS(event?.event_id);
  const countdownMode = countdownSettings?.countdown_mode === true;
  const { simulationMode, setSimulationMode } = useSimulationMode(event?.event_id);
  const { isOnline, lastReconnectAt } = useNetworkStatus();
  const comms = useComms(event?.event_id || null, "manager");

  const [athletes, setAthletes] = useState([]);
  const [judges, setJudges] = useState([]);

  // Toast on incoming reactions (manager view) — shows judge's first name when available
  useEffect(() => {
    if (!comms.lastReaction) return;
    const { judge_number, reaction_type } = comms.lastReaction;
    const emoji = { thumb_up: "👍", thumb_down: "👎", cross: "❌", help: "🆘" }[reaction_type] || "·";
    const isHelp = reaction_type === "help";
    const judge = judges.find(j => j.judge_number === judge_number);
    const displayName = judge?.first_name
      ? `${judge.first_name} (Juge ${judge_number})`
      : `Juge ${judge_number}`;
    const label = `${displayName} ${emoji}`;
    if (isHelp) {
      toast.error(label, { duration: 6000 });
    } else {
      toast(label, { duration: 3500 });
    }
    comms.clearLastReaction && comms.clearLastReaction();
    // eslint-disable-next-line
  }, [comms.lastReaction]);
  const [tournament, setTournament] = useState([]);
  const [battles, setBattles] = useState([]);
  const [currentBattle, setCurrentBattle] = useState(null);
  
  const [newAthlete, setNewAthlete] = useState({ name: "", photo_url: "", country: "", world_ranking: null, fid: "", gender: "Male", nextgen: false });
  const [newJudge, setNewJudge] = useState({ judge_number: 1, first_name: "", pin_code: "" });
  const [tournamentName, setTournamentName] = useState("");
  
  // Edit states
  const [editingAthlete, setEditingAthlete] = useState(null);
  const [editAthleteData, setEditAthleteData] = useState({ name: "", photo_url: "", country: "", world_ranking: null, fid: "", gender: "Male", nextgen: false });
  const [editingJudge, setEditingJudge] = useState(null);
  const [editJudgeData, setEditJudgeData] = useState({ judge_number: 1, first_name: "", pin_code: "" });
  
  // Theme editing state
  const [themeEdits, setThemeEdits] = useState({});
  const [selectedAthletes, setSelectedAthletes] = useState(new Set());
  const titleSizeTimerRef = useRef(null);
  
  // Auto-save title sizes with debounce (300ms)
  const autoSaveTitleSize = useCallback((key, value) => {
    if (titleSizeTimerRef.current) clearTimeout(titleSizeTimerRef.current);
    titleSizeTimerRef.current = setTimeout(() => {
      updateTheme({ [key]: value });
    }, 300);
  }, [updateTheme]);
  
  // Image upload states
  const [athleteImageFile, setAthleteImageFile] = useState(null);
  const [athleteImagePreview, setAthleteImagePreview] = useState(null);
  const [editAthleteImageFile, setEditAthleteImageFile] = useState(null);
  const [editAthleteImagePreview, setEditAthleteImagePreview] = useState(null);
  const [judgeImageFile, setJudgeImageFile] = useState(null);
  const [judgeImagePreview, setJudgeImagePreview] = useState(null);
  const [editJudgeImageFile, setEditJudgeImageFile] = useState(null);
  const [editJudgeImagePreview, setEditJudgeImagePreview] = useState(null);
  const [pendingCrop, setPendingCrop] = useState(null); // { file, target: 'add-athlete'|'edit-athlete'|'add-judge'|'edit-judge' }
  
  // Optional Battle states
  const [optionalBattle, setOptionalBattle] = useState(null);
  const [optionalAthlete1, setOptionalAthlete1] = useState("");
  const [optionalAthlete2, setOptionalAthlete2] = useState("");
  
  // Background image states
  
  // Judges count
  const [judgesCount, setJudgesCount] = useState(5);
  
  // Multi-tournament states
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState(null);
  const [tournamentFormat, setTournamentFormat] = useState("TOP16");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [collapsedBattles, setCollapsedBattles] = useState({});
  const [editingBattleAthletes, setEditingBattleAthletes] = useState({});
  const [selectedJudges, setSelectedJudges] = useState([]);
  const [showEditJudgesModal, setShowEditJudgesModal] = useState(false);
  const [editJudgesCount, setEditJudgesCount] = useState(5);
  const [editSelectedJudges, setEditSelectedJudges] = useState([]);
  const [focusedColorField, setFocusedColorField] = useState(null); // For highlighting preview elements
  const [athleteSortBy, setAthleteSortBy] = useState("name"); // "name", "ranking", "country", "gender"
  const [athleteFilterGender, setAthleteFilterGender] = useState("ALL"); // "ALL", "Male", "Female"
  const [forfeitModalOpen, setForfeitModalOpen] = useState(false);
  const [forfeitBattleId, setForfeitBattleId] = useState(null);
  const [forfeitWinner, setForfeitWinner] = useState("");
  const [qrJudge, setQrJudge] = useState(null);
  
  // Circle Qualification states
  const [tournamentType, setTournamentType] = useState("bracket");
  const [numCircles3, setNumCircles3] = useState(0);
  const [numCircles4, setNumCircles4] = useState(0);
  const [currentCircle, setCurrentCircle] = useState(null);
  const [tournamentCategory, setTournamentCategory] = useState("ALL");

  // Control tab: upcoming events & manual scoring
  const [upcomingSections, setUpcomingSections] = useState([]);
  const [manualBattleScores, setManualBattleScores] = useState({}); // { judgeNum: { a1: score, a2: score } }
  const [manualCircleScores, setManualCircleScores] = useState({}); // { judgeNum: { athleteId: score } }

  // Active tab (controlled, so keyboard shortcuts can switch tabs)
  const [activeTab, setActiveTab] = useState("athletes");

  // Helper function to get highlight style
  const getHighlightStyle = (fieldKeys) => {
    const isHighlighted = Array.isArray(fieldKeys) 
      ? fieldKeys.includes(focusedColorField)
      : fieldKeys === focusedColorField;
    
    return isHighlighted ? {
      outline: '3px solid #fbbf24',
      outlineOffset: '2px',
      transition: 'outline 0.2s ease',
      boxShadow: '0 0 20px rgba(251, 191, 36, 0.5)'
    } : {};
  };

  // Initial data load
  useEffect(() => {
    loadData();
  }, []);

  // Wrap loadCurrentBattle in useCallback for adaptive polling
  const pollCurrentBattle = useCallback(() => {
    // Skip network calls while offline: errors would spam the console and
    // the UI is already showing the last known good state thanks to fingerprinting.
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    loadCurrentBattle();
  }, []);

  // Adaptive polling: FAST during battle OR circle activity (not just battle).
  // Include pre_countdown/running/paused so the timer panel stays reactive
  // without triggering full re-renders thanks to fingerprinting below.
  const isBattleActive = currentBattle?.battle?.status === 'voting' || currentBattle?.battle?.status === 'revealing';
  const isCircleActive = currentCircle?.circle?.status === 'voting' || currentCircle?.circle?.status === 'revealing';
  useAdaptivePolling(pollCurrentBattle, isBattleActive || isCircleActive);

  const loadData = async () => {
    try {
      const [athletesRes, judgesRes, tournamentsRes] = await Promise.all([
        axios.get(`${API}/athletes`),
        axios.get(`${API}/judges`),
        axios.get(`${API}/tournaments`)  // Load ALL tournaments
      ]);
      // Fingerprint to avoid replacing identical state (which would otherwise
      // cause a full re-render of Athletes/Judges/Tournaments tabs every 20 s).
      const fp = loadDataFpRef.current;
      const aFp = (athletesRes.data || []).length + ':' + (athletesRes.data || []).map(a => `${a.id}:${a.updated_at || ''}:${a.name}:${a.photo_url || ''}:${a.country}:${a.world_ranking || ''}:${a.gender || ''}:${a.nextgen ? 1 : 0}`).join('|');
      const jFp = (judgesRes.data || []).length + ':' + (judgesRes.data || []).map(j => `${j.id}:${j.updated_at || ''}:${j.first_name}:${j.photo_url || ''}:${j.pin_code}`).join('|');
      const tFp = JSON.stringify((tournamentsRes.data || []).map(t => [t.id, t.name, t.judges_count, t.sort_order, t.current_round, t.status]));

      if (aFp !== fp.athletes) {
        fp.athletes = aFp;
        setAthletes(athletesRes.data);
      }
      if (jFp !== fp.judges) {
        fp.judges = jFp;
        setJudges(judgesRes.data);
      }
      
      // Multi-tournament support
      if (tournamentsRes.data && tournamentsRes.data.length > 0) {
        if (tFp !== fp.tournaments) {
          fp.tournaments = tFp;
          setTournaments(tournamentsRes.data);
        }

        // Preserve the user's current pick STRICTLY: if they already have a
        // selectedTournamentId that still exists in the fresh list, keep it.
        // Never silently fall back to `data[0]` — that was the "as soon as I
        // scroll the page jumps back to the first tournament" bug: any harmless
        // poll (e.g. after a bracket advances) would flip the selection.
        // Only auto-pick the first tournament on the very first load (when no
        // tournament has ever been selected).
        const stillHere = selectedTournamentId
          ? tournamentsRes.data.find(t => t.id === selectedTournamentId)
          : null;

        if (stillHere) {
          // The selected tournament still exists — leave the selection alone.
          // We deliberately do NOT call setTournament() here even if some
          // fields (name / judges_count / current_round) changed on the
          // server, because setTournament() causes the whole TournamentTab
          // to re-render and resets nested scroll/UI state. The
          // `tournaments` state has already been refreshed above, so the
          // tab list, headers, etc. will pick up the change on their own.
        } else if (!tournament) {
          // First load OR selected tournament was deleted → pick the first.
          const first = tournamentsRes.data[0];
          setTournament(first);
          setSelectedTournamentId(first.id);
          setJudgesCount(first.judges_count || 5);
          loadBattles(first.id);
          loadOptionalBattle(first.id);
        }
        // else: user had a selection that just vanished from the list;
        // leave the stale `tournament` in place — a user action will
        // pick a new one soon enough.
      } else if (tFp !== fp.tournaments) {
        fp.tournaments = tFp;
        setTournaments([]);
        setTournament(null);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  const loadBattles = async (tournamentId) => {
    try {
      const res = await axios.get(`${API}/tournament/${tournamentId}/battles`);
      setBattles(res.data);
      
      // Auto-collapse completed battles
      const newCollapsed = {};
      res.data.forEach(battle => {
        if (battle.status === 'completed' && collapsedBattles[battle.id] === undefined) {
          newCollapsed[battle.id] = true;
        }
      });
      if (Object.keys(newCollapsed).length > 0) {
        setCollapsedBattles(prev => ({ ...prev, ...newCollapsed }));
      }
    } catch (error) {
      console.error("Error loading battles:", error);
    }
  };

  // Keep fingerprint refs in sync when explicit user actions clear the
  // currentBattle/currentCircle state (finish, reset, etc.). This ensures
  // the next poll won't trigger the null-grace logic when the manager
  // already finalized the event intentionally.
  useEffect(() => {
    if (currentBattle === null && battleFpRef.current.id) {
      battleFpRef.current = { id: null, status: null, votes: 0, revealed: 0, sig: '' };
      battleNullStreakRef.current = 0;
    }
  }, [currentBattle]);
  useEffect(() => {
    if (currentCircle === null && circleFpRef.current.id) {
      circleFpRef.current = { id: null, status: null, votes: 0, revealed: 0, sig: '' };
      circleNullStreakRef.current = 0;
    }
  }, [currentCircle]);

  // Fingerprint refs to skip identical setState calls (prevents needless re-renders
  // that ripple down to ControlTab and visually shake the UI during a live event).
  // Fingerprints include vote count + revealed count + a checksum of vote contents
  // so that manual score overrides and forfeit toggles are still reflected.
  const battleFpRef = useRef({ id: null, status: null, votes: 0, revealed: 0, sig: '' });
  const circleFpRef = useRef({ id: null, status: null, votes: 0, revealed: 0, sig: '' });
  // Grace counters: when the server transiently returns null while we still
  // have an active battle/circle locally, hold the previous state for one
  // extra poll cycle before clearing. This eliminates the visible
  // "disappear → reappear" flicker on the Control tab caused by short-lived
  // race conditions (e.g. between battle.completed and the next battle
  // being started).
  const battleNullStreakRef = useRef(0);
  const circleNullStreakRef = useRef(0);
  const NULL_GRACE_POLLS = 2; // require N consecutive nulls before clearing
  const upcomingFpRef = useRef('');
  const loadDataFpRef = useRef({ athletes: 0, judges: 0, tournaments: '' });

  const _battleSig = (data) => {
    if (!data?.battle) return '';
    const b = data.battle;
    const votes = data.votes || [];
    return [
      b.winner_athlete_id || '',
      b.athlete1_id || '', b.athlete2_id || '',
      b.athlete1_forfeited ? '1' : '0', b.athlete2_forfeited ? '1' : '0',
      ...votes.map(v => `${v.judge_number}:${v.athlete1_score}:${v.athlete2_score}:${v.revealed ? 1 : 0}`),
    ].join('|');
  };
  const _circleSig = (data) => {
    if (!data?.circle) return '';
    const c = data.circle;
    const votes = data.votes || [];
    return [
      (c.forfeited_athletes || []).join(','),
      (c.athlete_ids || []).join(','),
      c.winner_athlete_id || '',
      ...votes.map(v => `${v.judge_number}:${v.revealed ? 1 : 0}:${JSON.stringify(v.scores || {})}`),
    ].join('|');
  };

  const loadCurrentBattle = async () => {
    try {
      const res = await axios.get(`${API}/battle/current`);
      const data = res.data || null;
      const newBattleFp = data?.battle ? {
        id: data.battle.id,
        status: data.battle.status,
        votes: (data.votes || []).length,
        revealed: (data.votes || []).filter(v => v.revealed).length,
        sig: _battleSig(data),
      } : { id: null, status: null, votes: 0, revealed: 0, sig: '' };

      // Grace period for transient nulls: if we had a battle locally and the
      // server now returns nothing, hold the previous state for N polls.
      if (!data && battleFpRef.current.id) {
        battleNullStreakRef.current += 1;
        if (battleNullStreakRef.current < NULL_GRACE_POLLS) {
          // skip this poll, keep currentBattle as-is
          return;
        }
      } else {
        battleNullStreakRef.current = 0;
      }

      const prev = battleFpRef.current;
      const changed = prev.id !== newBattleFp.id || prev.status !== newBattleFp.status || prev.votes !== newBattleFp.votes || prev.revealed !== newBattleFp.revealed || prev.sig !== newBattleFp.sig;
      if (changed) {
        battleFpRef.current = newBattleFp;
        setCurrentBattle(data);
      }
      if (data) {
        // Active battle → clear circle (only if currently set)
        if (circleFpRef.current.id) {
          circleFpRef.current = { id: null, status: null, votes: 0, revealed: 0, sig: '' };
          setCurrentCircle(null);
        }
      } else {
        // No battle active, check for active circle
        try {
          const circleRes = await axios.get(`${API}/circle/current`);
          const cdata = circleRes.data || null;
          const newCircleFp = cdata?.circle ? {
            id: cdata.circle.id,
            status: cdata.circle.status,
            votes: (cdata.votes || []).length,
            revealed: (cdata.votes || []).filter(v => v.revealed).length,
            sig: _circleSig(cdata),
          } : { id: null, status: null, votes: 0, revealed: 0, sig: '' };

          // Same grace logic for circles
          if (!cdata && circleFpRef.current.id) {
            circleNullStreakRef.current += 1;
            if (circleNullStreakRef.current < NULL_GRACE_POLLS) {
              return;
            }
          } else {
            circleNullStreakRef.current = 0;
          }

          const cprev = circleFpRef.current;
          const cchanged = cprev.id !== newCircleFp.id || cprev.status !== newCircleFp.status || cprev.votes !== newCircleFp.votes || cprev.revealed !== newCircleFp.revealed || cprev.sig !== newCircleFp.sig;
          if (cchanged) {
            circleFpRef.current = newCircleFp;
            setCurrentCircle(cdata);
          }
        } catch {
          // Network error: do NOT clear state to avoid block flickering.
        }
      }
    } catch (error) {
      console.error("Error loading current battle:", error);
      // Network/server transient error: keep the last known good state to
      // avoid unmounting the entire battle block on a single failed poll.
    }
  };

  // Shared pre-crop step: trusts the browser-provided file (Samsung Chrome
  // returns File objects with empty / "application/octet-stream" MIME from
  // the native camera, so we don't validate on MIME). Compression doubles
  // as the decode test — if the browser can't decode it, we show a clean
  // error. A success toast fires immediately so the manager always sees
  // feedback even if the crop dialog is slow to render on mobile.
  const _acceptPhoto = async (file, target) => {
    if (!file || file.size <= 0) return;
    try {
      const compressed = await compressImage(file);
      if (!compressed || compressed.size <= 0) throw new Error("decode-failed");
      if (compressed.size > 10 * 1024 * 1024) {
        toast.error(t('athletes.fileTooLarge'));
        return;
      }
      toast.success(t('athletes.photoReady'));
      setPendingCrop({ file: compressed, target });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("photo accept failed", err);
      toast.error(t('athletes.onlyPngJpeg'));
    }
  };

  // Shared "clear current photo" helper for both the Add and the Edit
  // flows of athletes and judges. Revokes any blob preview and resets
  // the stored photo_url to "" so the server-side record is wiped on save.
  const _deletePhotoFor = (target) => {
    const revoke = (url) => {
      if (url && typeof url === 'string' && url.startsWith('blob:')) {
        try { URL.revokeObjectURL(url); } catch { /* no-op */ }
      }
    };
    if (target === 'add-athlete') {
      revoke(athleteImagePreview);
      setAthleteImageFile(null);
      setAthleteImagePreview(null);
      setNewAthlete((p) => ({ ...p, photo_url: '' }));
    } else if (target === 'edit-athlete') {
      revoke(editAthleteImagePreview);
      setEditAthleteImageFile(null);
      setEditAthleteImagePreview(null);
      setEditAthleteData((p) => ({ ...p, photo_url: '' }));
    } else if (target === 'add-judge') {
      revoke(judgeImagePreview);
      setJudgeImageFile(null);
      setJudgeImagePreview(null);
      setNewJudge((p) => ({ ...p, photo_url: '' }));
    } else if (target === 'edit-judge') {
      revoke(editJudgeImagePreview);
      setEditJudgeImageFile(null);
      setEditJudgeImagePreview(null);
      setEditJudgeData((p) => ({ ...p, photo_url: '' }));
    }
  };

  const handleAthleteImageChange = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    _acceptPhoto(file, 'add-athlete');
  };

  const handleEditAthleteImageChange = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    _acceptPhoto(file, 'edit-athlete');
  };

  const handleJudgeImageChange = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    _acceptPhoto(file, 'add-judge');
  };

  const handleEditJudgeImageChange = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    _acceptPhoto(file, 'edit-judge');
  };

  const handleCropConfirm = (croppedFile) => {
    const target = pendingCrop?.target;
    // Helper: swap the preview blob URL and release the previous one so we
    // don't leak megabytes on iOS Safari between successive crops.
    const swapPreview = (setter) => {
      setter((old) => {
        if (old && typeof old === "string" && old.startsWith("blob:")) {
          try { URL.revokeObjectURL(old); } catch { /* no-op */ }
        }
        return URL.createObjectURL(croppedFile);
      });
    };
    if (target === 'add-athlete') {
      setAthleteImageFile(croppedFile);
      swapPreview(setAthleteImagePreview);
    } else if (target === 'edit-athlete') {
      setEditAthleteImageFile(croppedFile);
      swapPreview(setEditAthleteImagePreview);
    } else if (target === 'add-judge') {
      setJudgeImageFile(croppedFile);
      swapPreview(setJudgeImagePreview);
    } else if (target === 'edit-judge') {
      setEditJudgeImageFile(croppedFile);
      swapPreview(setEditJudgeImagePreview);
    }
    setPendingCrop(null);
  };

  const uploadImage = async (file) => {
    if (!file) return null;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data.url;
    } catch (error) {
      toast.error(t('common.error'));
      return null;
    }
  };

  const addAthlete = async () => {
    if (!newAthlete.name || !newAthlete.country) {
      toast.error(t('athletes.fillRequired'));
      return;
    }
    try {
      let photo_url = "";
      if (athleteImageFile) {
        photo_url = await uploadImage(athleteImageFile);
        if (!photo_url) return;
      }
      
      await axios.post(`${API}/athletes`, { ...newAthlete, photo_url });
      toast.success(t('athletes.added'));
      setNewAthlete({ name: "", photo_url: "", country: "", world_ranking: null, fid: "", gender: "Male", nextgen: false });
      setAthleteImageFile(null);
      setAthleteImagePreview(null);
      loadData();
    } catch (error) {
      toast.error(t('athletes.errorAdding'));
    }
  };
  
  const deleteImage = async (filename) => {
    if (!filename) return;
    try {
      // Extract filename from URL
      const parts = filename.split('/');
      const file = parts[parts.length - 1];
      await axios.delete(`${API}/upload/image/${file}`);
    } catch (error) {
      console.error("Error deleting image:", error);
    }
  };
  
  const handleBackgroundImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('athletes.onlyPngJpeg'));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('athletes.fileTooLarge'));
      return;
    }
    
    try {
      toast.info(t('settings.uploadingBackground'));
      
      // Upload image - uploadImage returns relative path like /api/uploads/xxx.jpg
      const relativeUrl = await uploadImage(file);
      
      if (relativeUrl) {
        // Update local state via updateTheme
        updateTheme({ background_image_url: relativeUrl });
        
        toast.success(t('settings.backgroundUploaded'));
      }
    } catch (error) {
      toast.error(t('common.error'));
      console.error(error);
    }
  };
  
  const deleteBackgroundImage = async () => {
    if (!window.confirm(t('settings.removeBackgroundConfirm'))) {
      return;
    }
    
    try {
      // Extract filename from URL to delete the file
      if (theme.background_image_url) {
        const filename = theme.background_image_url.split('/').pop();
        try {
          await axios.delete(`${API}/upload/image/${filename}`);
          console.log("Background image file deleted");
        } catch (e) {
          console.log("Image file already deleted or not found:", e.message);
          // Continue anyway to clean the database
        }
      }
      
      // Update theme in database to remove background_image_url
      // Use empty string to ensure it's cleared
      await updateTheme({ background_image_url: "" });
      
      toast.success(t('settings.backgroundRemoved'));
    } catch (error) {
      toast.error(t('common.error'));
      console.error("Delete background error:", error);
    }
  };

  const deleteAthlete = async (id) => {
    try {
      await axios.delete(`${API}/athletes/${id}`);
      toast.success(t('athletes.deleted'));
      loadData();
    } catch (error) {
      toast.error(t('athletes.errorDeleting'));
    }
  };

  const deleteSelectedAthletes = async () => {
    if (selectedAthletes.size === 0) return;
    if (!window.confirm(t('athletes.deleteConfirmBulk', { count: selectedAthletes.size }))) return;
    try {
      await axios.post(`${API}/athletes/delete-bulk`, { ids: [...selectedAthletes] });
      toast.success(t('athletes.deletedBulk', { count: selectedAthletes.size }));
      setSelectedAthletes(new Set());
      loadData();
    } catch (error) {
      toast.error(t('athletes.errorDeleting'));
    }
  };

  const toggleAthleteSelection = (id) => {
    setSelectedAthletes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  
  const deleteJudge = async (judgeNumber) => {
    if (!window.confirm(t('judges.deleteConfirm', { num: judgeNumber }))) {
      return;
    }
    try {
      await axios.delete(`${API}/judges/${judgeNumber}`);
      toast.success(t('judges.deleted'));
      loadData();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };
  
  const deleteJudgePhoto = async (judge) => {
    if (judge.photo_url) {
      await deleteImage(judge.photo_url);
    }
    try {
      await axios.put(`${API}/judges/${judge.judge_number}`, {
        judge_number: judge.judge_number,
        first_name: judge.first_name,
        pin_code: judge.pin_code,
        photo_url: ""
      });
      toast.success(t('judges.photoDeleted'));
      loadData();
    } catch (error) {
      toast.error(t('judges.errorDeletingPhoto'));
    }
  };

  const openEditAthlete = (athlete) => {
    setEditingAthlete(athlete);
    setEditAthleteData({ name: athlete.name, photo_url: athlete.photo_url, country: athlete.country, world_ranking: athlete.world_ranking || null, fid: athlete.fid || "", gender: athlete.gender || "Male", nextgen: athlete.nextgen || false });
    setEditAthleteImageFile(null);
    setEditAthleteImagePreview(athlete.photo_url || null);
  };

  const updateAthlete = async () => {
    if (!editAthleteData.name || !editAthleteData.country) {
      toast.error(t('athletes.fillRequired'));
      return;
    }
    try {
      let photo_url = editAthleteData.photo_url;
      if (editAthleteImageFile) {
        photo_url = await uploadImage(editAthleteImageFile);
        if (!photo_url) return;
      }
      
      await axios.put(`${API}/athletes/${editingAthlete.id}`, { ...editAthleteData, photo_url });
      toast.success(t('athletes.updated'));
      setEditingAthlete(null);
      setEditAthleteImageFile(null);
      setEditAthleteImagePreview(null);
      loadData();
    } catch (error) {
      toast.error(t('athletes.errorUpdating'));
    }
  };

  const addJudge = async () => {
    if (!newJudge.first_name || !newJudge.pin_code) {
      toast.error(t('judges.fillRequired'));
      return;
    }
    try {
      let photo_url = "";
      if (judgeImageFile) {
        photo_url = await uploadImage(judgeImageFile);
        if (!photo_url) return;
      }
      
      await axios.post(`${API}/judges`, { ...newJudge, photo_url });
      toast.success(t('judges.added'));
      setNewJudge({ judge_number: newJudge.judge_number + 1, first_name: "", pin_code: "" });
      setJudgeImageFile(null);
      setJudgeImagePreview(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('judges.errorAdding'));
    }
  };

  const openEditJudge = (judge) => {
    setEditingJudge(judge);
    setEditJudgeData({ judge_number: judge.judge_number, first_name: judge.first_name, pin_code: judge.pin_code, photo_url: judge.photo_url || "" });
    setEditJudgeImageFile(null);
    setEditJudgeImagePreview(judge.photo_url || null);
  };

  const updateJudge = async () => {
    if (!editJudgeData.first_name || !editJudgeData.pin_code) {
      toast.error(t('judges.fillRequired'));
      return;
    }
    try {
      let photo_url = editJudgeData.photo_url;
      if (editJudgeImageFile) {
        photo_url = await uploadImage(editJudgeImageFile);
        if (!photo_url) return;
      }
      
      await axios.put(`${API}/judges/${editingJudge.judge_number}`, { ...editJudgeData, photo_url });
      toast.success(t('judges.updated'));
      setEditingJudge(null);
      setEditJudgeImageFile(null);
      setEditJudgeImagePreview(null);
      loadData();
    } catch (error) {
      toast.error(t('judges.errorUpdating'));
    }
  };

  const createTournament = async () => {
    if (!tournamentName) {
      toast.error(t('athletes.fillRequired'));
      return;
    }
    
    // Validation: check judges selection
    if (selectedJudges.length !== judgesCount) {
      toast.error(t('tournament.selectJudges'));
      return;
    }
    
    if (tournamentType === "circle_qualification") {
      // Circle Qualification validation
      if (numCircles3 + numCircles4 === 0) {
        toast.error(t('tournament.circleConfig'));
        return;
      }
      
      try {
        await axios.post(`${API}/tournament`, {
          name: tournamentName,
          type: "circle_qualification",
          category: tournamentCategory,
          num_circles_3: numCircles3,
          num_circles_4: numCircles4,
          judges_count: judgesCount,
          assigned_judge_numbers: selectedJudges
        });
        toast.success(t('tournament.circleCreated'));
        await loadData();
        setTournamentName("");
        setSelectedJudges([]);
        setNumCircles3(0);
        setNumCircles4(0);
        setShowCreateForm(false);
      } catch (error) {
        toast.error(error.response?.data?.detail || t('tournament.errorCreating'));
      }
      return;
    }

    if (tournamentType === "1vs1_battles") {
      // 1v1 Battles: no athlete count requirement (manager will add battles afterwards)
      try {
        await axios.post(`${API}/tournament`, {
          name: tournamentName,
          type: "1vs1_battles",
          category: tournamentCategory,
          judges_count: judgesCount,
          assigned_judge_numbers: selectedJudges
        });
        toast.success(t('tournament.created'));
        await loadData();
        setTournamentName("");
        setSelectedJudges([]);
        setShowCreateForm(false);
      } catch (error) {
        toast.error(error.response?.data?.detail || t('tournament.errorCreating'));
      }
      return;
    }
    
    // Bracket tournament validation
    const requiredAthletes = {
      "TOP32": 32,
      "TOP16": 16,
      "TOP8": 8,
      "TOP4": 4
    };
    
    const required = requiredAthletes[tournamentFormat];
    const eligibleAthletes = athletes.filter(a => {
      if (tournamentCategory === 'ALL') return true;
      if (tournamentCategory === 'Male') return a.gender === 'Male';
      if (tournamentCategory === 'Female') return a.gender === 'Female';
      if (tournamentCategory === 'NextGen') return a.nextgen === true;
      return true;
    });
    if (eligibleAthletes.length < required) {
      toast.error(t('tournament.needAthletes', { count: required, category: tournamentCategory !== 'ALL' ? tournamentCategory : '', format: tournamentFormat, found: eligibleAthletes.length }));
      return;
    }
    
    try {
      await axios.post(`${API}/tournament`, { 
        name: tournamentName,
        type: "bracket",
        category: tournamentCategory,
        format: tournamentFormat,
        judges_count: judgesCount,
        assigned_judge_numbers: selectedJudges
      });
      toast.success(t('tournament.created'));
      await loadData();
      setTournamentName("");
      setSelectedJudges([]);
    } catch (error) {
      toast.error(t('tournament.errorCreating'));
    }
  };

  const updateBattleAthletes = async (battleId, athlete1Id, athlete2Id) => {
    if (!athlete1Id || !athlete2Id) {
      toast.error(t('battle.selectBoth'));
      return;
    }
    // BYE is a valid sentinel: BYE vs BYE is allowed (edge case: both slots
    // are byes and the walkover propagates as BYE to the next round). Two
    // real athletes must still be distinct.
    if (athlete1Id === athlete2Id && athlete1Id !== "BYE") {
      toast.error(t('battle.selectDifferent'));
      return;
    }
    
    try {
      await axios.put(`${API}/battle/${battleId}/athletes`, null, {
        params: { athlete1_id: athlete1Id, athlete2_id: athlete2Id }
      });
      toast.success(t('tournament.athletesAssigned'));
      if (tournament?.id) {
        loadBattles(tournament.id);
      }
      setEditingBattleAthletes(prev => {
        const newState = {...prev};
        delete newState[battleId];
        return newState;
      });
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Skip a pending battle that has at least one BYE slot. The backend
  // auto-completes it with the correct walkover winner and advances the
  // bracket. If both slots are BYE the winner is "BYE" itself, so the
  // next-round card will show "BYE" until the manager decides what to do.
  const skipBattle = async (battleId) => {
    try {
      await axios.post(`${API}/battle/${battleId}/skip`);
      toast.success(t('battle.skipped') || 'Battle skipped');
      if (tournament?.id) {
        loadBattles(tournament.id);
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || t('common.error'));
    }
  };

  const _hasInProgressEvent = () => {
    if (currentBattle && currentBattle.battle.status !== 'completed') return 'battle';
    if (currentCircle && currentCircle.circle.status !== 'completed') return 'circle';
    return null;
  };

  const startBattle = async (battleId) => {
    const inProg = _hasInProgressEvent();
    if (inProg) {
      toast.error(inProg === 'battle' ? t('timer.errorBattleInProgress') : t('timer.errorCircleInProgressForBattle'));
      return;
    }

    try {
      await axios.post(`${API}/battle/${battleId}/start`);
      toast.success(t('battle.started'));
      if (tournament?.id) {
        loadBattles(tournament.id);
        loadOptionalBattle(tournament.id);
      }
      loadCurrentBattle();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('battle.errorStarting'));
    }
  };

  const startCircle = async (circleId) => {
    const inProg = _hasInProgressEvent();
    if (inProg) {
      toast.error(inProg === 'circle' ? t('timer.errorCircleInProgress') : t('timer.errorBattleInProgressForCircle'));
      return false;
    }
    try {
      await axios.post(`${API}/circle/${circleId}/start`);
      toast.success(t('circle.started'));
      loadCurrentBattle();
      loadUpcomingEvents();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
      return false;
    }
  };

  const revealVote = async (voteIndex) => {
    if (!currentBattle) return;
    try {
      await axios.post(`${API}/battle/${currentBattle.battle.id}/reveal`, {
        battle_id: currentBattle.battle.id,
        vote_index: voteIndex
      });
      toast.success(t('control.revealVote'));
      loadCurrentBattle();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const revealAllVotes = async () => {
    if (!currentBattle) return;
    try {
      await axios.post(`${API}/battle/${currentBattle.battle.id}/reveal-all`);
      toast.success(t('control.allRevealed'));
      loadCurrentBattle();
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const finishBattle = async () => {
    if (!currentBattle) return;
    try {
      const res = await axios.post(`${API}/battle/${currentBattle.battle.id}/finish`);
      const winnerName = res.data.winner_id === currentBattle.athlete1.id 
        ? currentBattle.athlete1.name 
        : currentBattle.athlete2.name;
      toast.success(t('control.winnerAnnounce').replace('{name}', winnerName).replace('{score1}', res.data.athlete1_total_score).replace('{score2}', res.data.athlete2_total_score));
      if (tournament?.id) {
        loadBattles(tournament.id);
        loadOptionalBattle(tournament.id);
      }
      setCurrentBattle(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('control.errorFinishing'));
    }
  };

  // Get the next battle/circle across all tournaments, following section order
  const getNextEvent = useCallback(() => {
    for (const section of upcomingSections) {
      if (section.circles.length > 0) {
        const c = section.circles.find(c => c.status === 'pending' && c.athlete_ids?.filter(a => a).length >= 2);
        if (c) return { type: 'circle', id: c.id, tournamentId: section.tournament_id };
      }
      if (section.battles.length > 0) {
        const b = section.battles.find(b => b.status === 'pending' && b.athlete1_id && b.athlete2_id);
        if (b) return { type: 'battle', id: b.id, tournamentId: section.tournament_id };
      }
    }
    return null;
  }, [upcomingSections]);

  const getNextBattle = () => {
    const next = getNextEvent();
    return next ? true : null;
  };

  const finishBattleAndNext = async () => {
    if (!currentBattle) return;
    try {
      const res = await axios.post(`${API}/battle/${currentBattle.battle.id}/finish`);
      const winnerName = res.data.winner_id === currentBattle.athlete1.id 
        ? currentBattle.athlete1.name 
        : currentBattle.athlete2.name;
      toast.success(t('control.winnerAnnounce').replace('{name}', winnerName).replace('{score1}', res.data.athlete1_total_score).replace('{score2}', res.data.athlete2_total_score));
      
      // Reload upcoming to get fresh state after finish
      await loadUpcomingEvents();
      
      // Use getNextEvent from the freshly loaded sections won't work (stale closure)
      // So fetch upcoming directly
      const upRes = await axios.get(`${API}/upcoming-events`);
      const freshSections = upRes.data || [];
      let started = false;
      for (const section of freshSections) {
        for (const c of section.circles) {
          if (c.status === 'pending' && c.athlete_ids?.filter(a => a).length >= 2) {
            await axios.post(`${API}/circle/${c.id}/start`);
            toast.success(t('circle.started'));
            started = true; break;
          }
        }
        if (started) break;
        for (const b of section.battles) {
          if (b.status === 'pending' && b.athlete1_id && b.athlete2_id) {
            await axios.post(`${API}/battle/${b.id}/start`);
            toast.success(t('battle.started'));
            started = true; break;
          }
        }
        if (started) break;
      }
      
      await loadCurrentBattle();
      await loadUpcomingEvents();
      if (tournament?.id) { loadBattles(tournament.id); loadOptionalBattle(tournament.id); }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('control.errorFinishing'));
    }
  };

  const finishCircleAndNext = async (circleId) => {
    try {
      await axios.post(`${API}/circle/${circleId}/finish`);
      toast.success(t('circle.finished'));
      setCurrentCircle(null);

      await loadUpcomingEvents();
      const upRes = await axios.get(`${API}/upcoming-events`);
      const freshSections = upRes.data || [];
      let started = false;
      for (const section of freshSections) {
        for (const c of section.circles) {
          if (c.status === 'pending' && c.athlete_ids?.filter(a => a).length >= 2) {
            await axios.post(`${API}/circle/${c.id}/start`);
            toast.success(t('circle.started'));
            started = true; break;
          }
        }
        if (started) break;
        for (const b of section.battles) {
          if (b.status === 'pending' && b.athlete1_id && b.athlete2_id) {
            await axios.post(`${API}/battle/${b.id}/start`);
            toast.success(t('battle.started'));
            started = true; break;
          }
        }
        if (started) break;
      }

      await loadCurrentBattle();
      await loadUpcomingEvents();
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('control.errorFinishCircle'));
    }
  };



  const resetBattle = async (battleId) => {
    // Use safeConfirm (in-page toast) instead of native window.confirm(),
    // which would exit fullscreen on any tab of the same browser process.
    const ok = await safeConfirm(t('control.resetBattleConfirm'));
    if (!ok) return;
    try {
      // Perform the reset on backend FIRST
      await axios.post(`${API}/battle/${battleId}/reset`);
      toast.success(t('battle.resetDone'));

      // Reload tournament-specific data only (avoid full loadData which can flicker UI)
      if (tournament?.id) {
        await Promise.all([
          loadBattles(tournament.id),
          loadOptionalBattle(tournament.id),
        ]);
      }
      // Refresh current battle (will clear if it was the one reset)
      await loadCurrentBattle();
    } catch (error) {
      toast.error(t('common.error'));
      console.error("Reset battle error:", error);
    }
  };
  
  // Circle control functions for Control tab
  const circleRevealVote = async (circleId, voteIndex) => {
    try {
      await axios.post(`${API}/circle/${circleId}/reveal/${voteIndex}`);
      loadCurrentBattle();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const circleRevealAll = async (circleId) => {
    try {
      await axios.post(`${API}/circle/${circleId}/reveal-all`);
      toast.success(t('control.allRevealed'));
      loadCurrentBattle();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('control.errorRevealAll'));
    }
  };

  const circleFinish = async (circleId) => {
    try {
      const res = await axios.post(`${API}/circle/${circleId}/finish`);
      toast.success(t('circle.finished'));
      setCurrentCircle(null);
      loadCurrentBattle();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('control.errorFinishCircle'));
    }
  };

  const circleReset = async (circleId) => {
    // Use safeConfirm to preserve Big Screen fullscreen state.
    const ok = await safeConfirm(t('control.resetBattleConfirm'));
    if (!ok) return;
    try {
      await axios.post(`${API}/circle/${circleId}/reset`);
      toast.success(t('circle.resetDone'));
      setCurrentCircle(null);
      loadCurrentBattle();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const circleDeleteVote = async (circleId, judgeId) => {
    const ok = await safeConfirm(t('common.confirm'));
    if (!ok) return;
    try {
      await axios.delete(`${API}/circle/${circleId}/vote/${judgeId}`);
      toast.success(t('common.success'));
      loadCurrentBattle();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const circleForfeit = async (circleId, athleteId) => {
    const ok = await safeConfirm(t('circle.forfeitConfirm'));
    if (!ok) return;
    try {
      await axios.post(`${API}/circle/${circleId}/forfeit`, { athlete_id: athleteId });
      toast.success(t('circle.forfeited'));
      loadCurrentBattle();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const circleCancelForfeit = async (circleId, athleteId) => {
    try {
      await axios.post(`${API}/circle/${circleId}/cancel-forfeit`, { athlete_id: athleteId });
      toast.success(t('circle.forfeitCancelled'));
      loadCurrentBattle();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  // Load upcoming battles/circles for the Control tab (all tournaments).
  // Fingerprinted to avoid re-rendering the whole tab every 5 s with identical data.
  const loadUpcomingEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/upcoming-events`);
      const next = res.data || [];
      const fp = JSON.stringify(next.map(s => [
        s.tournament_id,
        s.tournament_name,
        (s.battles || []).map(b => [b.id, b.status, b.athlete1_id, b.athlete2_id]),
        (s.circles || []).map(c => [c.id, c.status, c.circle_number]),
      ]));
      if (fp !== upcomingFpRef.current) {
        upcomingFpRef.current = fp;
        setUpcomingSections(next);
      }
    } catch { /* keep last known good state */ }
  }, []);

  // Reload upcoming on initial mount + when battle/circle TRANSITIONS (id/status change).
  // We deliberately do NOT depend on the full currentBattle/currentCircle objects:
  // those references change on every vote/reveal but the upcoming queue does not.
  const transitionKeyRef = useRef('');
  const transitionKey = `${currentBattle?.battle?.id || ''}:${currentBattle?.battle?.status || ''}|${currentCircle?.circle?.id || ''}:${currentCircle?.circle?.status || ''}`;
  useEffect(() => {
    if (transitionKey !== transitionKeyRef.current) {
      transitionKeyRef.current = transitionKey;
      loadUpcomingEvents();
    }
  }, [transitionKey, loadUpcomingEvents]);

  // Auto-refresh data when the connection comes back online: gives the
  // manager an instant "all good" feeling after a wifi blip without
  // requiring a manual CTRL+R.
  useEffect(() => {
    if (!lastReconnectAt) return;
    loadUpcomingEvents();
    if (battleFpRef.current.id || circleFpRef.current.id) {
      loadCurrentBattle();
    }
  }, [lastReconnectAt, loadUpcomingEvents]);

  // Background auto-refresh — keeps the Control tab (and the rest of the page)
  // in sync when the manager makes changes in TournamentTab/AthletesTab/JudgesTab
  // without forcing a hard reload (CTRL+R). Three layers:
  //   1) Upcoming events polled every 5 s (cheap, drives the Control tab queue).
  //   2) Athletes/Judges/Tournaments polled every 20 s (heavier endpoints).
  //   3) On tab visibility regain → immediate full refresh.
  useEffect(() => {
    if (!event?.event_id) return undefined;
    const fastIv = setInterval(() => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      loadUpcomingEvents();
    }, 5000);
    // Heavier refresh (athletes/judges/tournaments) — skip while a battle or
    // circle is active to prevent the UI from "shifting" during a live event.
    // Also skipped while offline (saves bandwidth and avoids console spam).
    // Tab-visibility regain handler below will catch any missed update.
    const slowIv = setInterval(() => {
      if (typeof navigator !== 'undefined' && !navigator.onLine) return;
      const bs = battleFpRef.current.status;
      const cs = circleFpRef.current.status;
      const battleLive = bs === 'voting' || bs === 'revealing';
      const circleLive = cs === 'voting' || cs === 'revealing';
      if (battleLive || circleLive) return;
      loadData();
    }, 20000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && (typeof navigator === 'undefined' || navigator.onLine)) {
        loadUpcomingEvents();
        loadData();
        if (currentBattle?.battle?.id || currentCircle?.circle?.id) {
          loadCurrentBattle();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onVisibility);
    return () => {
      clearInterval(fastIv);
      clearInterval(slowIv);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onVisibility);
    };
    // eslint-disable-next-line
  }, [event?.event_id, loadUpcomingEvents]);

  // ========== KEYBOARD SHORTCUTS - global handlers ==========
  // Reveal vote of judge N for the current battle (or circle)
  const revealVoteByJudgeNumber = useCallback((judgeNumber) => {
    const votes = currentBattle?.votes || currentCircle?.votes || [];
    const sorted = [...votes].sort((a, b) => a.judge_number - b.judge_number);
    const idx = sorted.findIndex((v) => v.judge_number === judgeNumber);
    if (idx < 0) {
      toast.error(t('shortcuts.errorNoVoteForJudge').replace('{n}', judgeNumber));
      return;
    }
    if (currentBattle?.battle?.id) {
      axios.post(`${API}/battle/${currentBattle.battle.id}/reveal`, {
        battle_id: currentBattle.battle.id, vote_index: idx,
      }).then(() => loadCurrentBattle()).catch(err => toast.error(err.response?.data?.detail || t('common.error')));
    } else if (currentCircle?.circle?.id) {
      axios.post(`${API}/circle/${currentCircle.circle.id}/reveal/${idx}`)
        .then(() => loadCurrentBattle()).catch(err => toast.error(err.response?.data?.detail || t('common.error')));
    }
  }, [currentBattle, currentCircle, t]);

  // Reveal next pending vote (the next un-revealed one in judge_number order)
  const revealNextPending = useCallback(() => {
    const votes = currentBattle?.votes || currentCircle?.votes || [];
    if (votes.length === 0) {
      toast.error(t('shortcuts.errorNoVotesYet'));
      return;
    }
    const sorted = [...votes].sort((a, b) => a.judge_number - b.judge_number);
    const idx = sorted.findIndex((v) => !v.revealed);
    if (idx < 0) {
      toast.info(t('shortcuts.allRevealed'));
      return;
    }
    if (currentBattle?.battle?.id) {
      axios.post(`${API}/battle/${currentBattle.battle.id}/reveal`, {
        battle_id: currentBattle.battle.id, vote_index: idx,
      }).then(() => loadCurrentBattle()).catch(err => toast.error(err.response?.data?.detail || t('common.error')));
    } else if (currentCircle?.circle?.id) {
      axios.post(`${API}/circle/${currentCircle.circle.id}/reveal/${idx}`)
        .then(() => loadCurrentBattle()).catch(err => toast.error(err.response?.data?.detail || t('common.error')));
    }
  }, [currentBattle, currentCircle, t]);

  // Forfeit one of the two athletes of the current battle (winner = the OTHER)
  const forfeitCurrentBattle = useCallback((forfeitedSide) => {
    if (!currentBattle?.battle?.id) {
      toast.error(t('shortcuts.errorNoBattle'));
      return;
    }
    const winner_id = forfeitedSide === 1 ? currentBattle.battle.athlete2_id : currentBattle.battle.athlete1_id;
    if (!winner_id) return;
    axios.post(`${API}/battle/${currentBattle.battle.id}/forfeit`, { winner_id })
      .then(() => { toast.success(t('battle.forfeited')); loadCurrentBattle(); })
      .catch(err => toast.error(err.response?.data?.detail || t('common.error')));
  }, [currentBattle, t]);

  // Toggle countdown_mode at event level
  const toggleCountdownMode = useCallback(async () => {
    if (!event?.event_id) return;
    try {
      const newVal = !countdownMode;
      await axios.put(`${API}/event/${event.event_id}/countdown-settings`, { countdown_mode: newVal });
      toast.success(newVal ? t('shortcuts.countdownEnabled') : t('shortcuts.countdownDisabled'));
    } catch {
      toast.error(t('common.error'));
    }
  }, [event?.event_id, countdownMode, t]);

  // Toggle simulation mode
  const toggleSimulationMode = useCallback(async () => {
    try {
      await setSimulationMode(!simulationMode);
      toast.success(!simulationMode ? t('dryRun.enabled') : t('dryRun.disabled'));
    } catch {
      toast.error(t('common.error'));
    }
  }, [simulationMode, setSimulationMode, t]);

  useKeyboardShortcuts({
    // Battle / Circle control
    startEvent: () => {
      const next = getNextEvent && getNextEvent();
      if (!next) { toast.error(t('shortcuts.errorNoNext')); return; }
      if (next.type === 'battle') startBattle(next.id);
      else if (next.type === 'circle') startCircle(next.id);
    },
    revealJ1: () => revealVoteByJudgeNumber(1),
    revealJ2: () => revealVoteByJudgeNumber(2),
    revealJ3: () => revealVoteByJudgeNumber(3),
    revealJ4: () => revealVoteByJudgeNumber(4),
    revealJ5: () => revealVoteByJudgeNumber(5),
    revealJ6: () => revealVoteByJudgeNumber(6),
    revealJ7: () => revealVoteByJudgeNumber(7),
    revealJ8: () => revealVoteByJudgeNumber(8),
    revealJ9: () => revealVoteByJudgeNumber(9),
    revealJ10: () => revealVoteByJudgeNumber(10),
    revealNext: () => revealNextPending(),
    revealAll: () => {
      if (currentBattle?.battle?.id) revealAllVotes();
      else if (currentCircle?.circle?.id) circleRevealAll(currentCircle.circle.id);
    },
    finishEvent: () => {
      if (currentBattle?.battle?.id) finishBattle();
      else if (currentCircle?.circle?.id) circleFinish(currentCircle.circle.id);
    },
    resetEvent: () => {
      if (currentBattle?.battle?.id) resetBattle(currentBattle.battle.id);
      else if (currentCircle?.circle?.id) circleReset(currentCircle.circle.id);
    },
    forfeitA1: () => forfeitCurrentBattle(1),
    forfeitA2: () => forfeitCurrentBattle(2),
    // Mode toggles
    toggleCountdown: toggleCountdownMode,
    toggleSimulation: toggleSimulationMode,
    nextEvent: () => {
      const next = getNextEvent && getNextEvent();
      if (!next) { toast.error(t('shortcuts.errorNoNext')); return; }
      if (next.type === 'battle') startBattle(next.id);
      else if (next.type === 'circle') startCircle(next.id);
    },
    // Tab navigation
    tabAthletes: () => setActiveTab('athletes'),
    tabJudges: () => setActiveTab('judges'),
    tabTournament: () => setActiveTab('tournament'),
    tabControl: () => setActiveTab('control'),
    tabSettings: () => setActiveTab('settings'),
  });

  // Reorder tournament sections
  const reorderSections = async (newOrder) => {
    setUpcomingSections(prev => {
      const map = {};
      prev.forEach(s => { map[s.tournament_id] = s; });
      return newOrder.map(tid => map[tid]).filter(Boolean);
    });
    try {
      await axios.put(`${API}/tournaments/reorder`, { order: newOrder });
    } catch { toast.error(t('common.error')); }
  };

  // Manager submits a vote on behalf of a judge (battle)
  const submitManagerBattleVote = async (judgeNum) => {
    if (!currentBattle) return;
    const scores = manualBattleScores[judgeNum];
    if (!scores?.a1 || !scores?.a2) { toast.error(t('control.selectBothScores')); return; }
    try {
      await axios.post(`${API}/battle/${currentBattle.battle.id}/manager-vote`, {
        judge_number: judgeNum,
        athlete1_score: scores.a1,
        athlete2_score: scores.a2
      });
      toast.success(t('control.managerVoteSubmitted'));
      setManualBattleScores(prev => { const n = {...prev}; delete n[judgeNum]; return n; });
      loadCurrentBattle();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  // Manager submits a vote on behalf of a judge (circle)
  const submitManagerCircleVote = async (judgeNum) => {
    if (!currentCircle) return;
    const scores = manualCircleScores[judgeNum];
    if (!scores || Object.keys(scores).length === 0) { toast.error(t('control.selectAllScores')); return; }
    try {
      const intScores = {};
      for (const [k, v] of Object.entries(scores)) { intScores[k] = parseInt(v); }
      await axios.post(`${API}/circle/${currentCircle.circle.id}/manager-vote`, {
        judge_number: judgeNum,
        scores: intScores
      });
      toast.success(t('control.managerVoteSubmitted'));
      setManualCircleScores(prev => { const n = {...prev}; delete n[judgeNum]; return n; });
      loadCurrentBattle();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const loadOptionalBattle = async (tournamentId) => {
    try {
      const res = await axios.get(`${API}/battle/optional/${tournamentId}`);
      setOptionalBattle(res.data);
    } catch (error) {
      console.error("Error loading optional battle:", error);
    }
  };
  
  const createOptionalBattle = async () => {
    if (!optionalAthlete1 || !optionalAthlete2) {
      toast.error(t('battle.selectBoth'));
      return;
    }
    if (optionalAthlete1 === optionalAthlete2) {
      toast.error(t('battle.selectDifferent'));
      return;
    }
    try {
      await axios.post(`${API}/battle/optional`, {
        tournament_id: tournament.id,
        athlete1_id: optionalAthlete1,
        athlete2_id: optionalAthlete2
      });
      toast.success(t('common.success'));
      setOptionalAthlete1("");
      setOptionalAthlete2("");
      loadOptionalBattle(tournament.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const getCategoryFilteredAthletes = () => {
    const cat = tournament?.category || 'ALL';
    if (cat === 'ALL') return athletes;
    return athletes.filter(a => {
      if (cat === 'Male') return a.gender === 'Male';
      if (cat === 'Female') return a.gender === 'Female';
      if (cat === 'NextGen') return a.nextgen === true;
      return true;
    });
  };

  const getAvailableAthletes = (excludeBattleId = null, excludeAthleteId = null) => {
    const assignedAthleteIds = battles
      .filter(b => b.id !== excludeBattleId && b.tournament_id === tournament?.id)
      .flatMap(b => [b.athlete1_id, b.athlete2_id])
      .filter(id => id && id !== "" && id !== "BYE");

    // Always expose BYE at the top of the list, in every tournament type.
    // BYE is never "consumed" by a battle assignment, so it can appear on
    // as many slots as needed (BYE vs BYE is a valid walkover pair).
    const BYE_OPTION = { id: "BYE", name: "BYE", country: "", world_ranking: null };

    const realAthletes = getCategoryFilteredAthletes().filter(a =>
      !assignedAthleteIds.includes(a.id) &&
      a.id !== excludeAthleteId
    );

    return [BYE_OPTION, ...realAthletes];
  };

  const openEditJudgesModal = () => {
    if (!tournament) return;
    setEditJudgesCount(tournament.judges_count || 5);
    setEditSelectedJudges(tournament.assigned_judge_numbers || []);
    setShowEditJudgesModal(true);
  };

  const updateTournamentJudges = async () => {
    if (!tournament) return;
    
    if (editSelectedJudges.length !== editJudgesCount) {
      toast.error(t('tournament.selectJudges'));
      return;
    }
    
    try {
      await axios.put(`${API}/tournament/${tournament.id}/judges`, null, {
        params: {
          judges_count: editJudgesCount,
          assigned_judge_numbers: editSelectedJudges.join(',')
        }
      });
      toast.success(t('judges.judgesModified'));
      setShowEditJudgesModal(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const deleteOptionalBattle = async () => {
    if (!tournament) return;
    if (!window.confirm(t('battle.deleteOptionalConfirm'))) {
      return;
    }
    try {
      await axios.delete(`${API}/battle/optional/${tournament.id}`);
      toast.success(t('battle.optionalDeleted'));
      setOptionalBattle(null);
      setOptionalAthlete1("");
      setOptionalAthlete2("");
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const deleteTournament = async () => {
    if (!tournament) return;
    if (!window.confirm(t('tournament.deleteConfirm'))) {
      return;
    }
    try {
      await axios.delete(`${API}/tournament/${tournament.id}`);
      toast.success(t('tournament.deleted'));
      setTournament(null);
      setBattles([]);
      setCurrentBattle(null);
      loadData();
    } catch (error) {
      toast.error(t('tournament.errorDeleting'));
    }
  };

  const getRoundLabel = (round) => {
    const labels = {
      "TOP32": t('rounds.top32'),
      "TOP16": t('rounds.top16'),
      "QUARTER_FINALS": t('rounds.quarterFinals'),
      "SEMI_FINALS": t('rounds.semiFinals'),
      "FINAL": t('rounds.final'),
      "THIRD_PLACE": t('rounds.thirdPlace'),
      "CIRCLE": t('rounds.circle')
    };
    return labels[round] || round;
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success(t('common.copied') || 'Copied'); };
  const baseUrl = window.location.origin;

  return (
    <div className="min-h-screen p-6 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')} className="text-muted-foreground hover:text-foreground" data-testid="back-to-admin">
              <ArrowLeft size={18} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground tracking-tight" data-testid="director-title">
                {event?.name || 'Event Dashboard'}
              </h1>
            </div>
          </div>
          {/* Quick Actions */}
          {slug && (
            <div className="flex items-center gap-2">
              {!isOnline && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-red-500/15 border border-red-500/40 text-red-500 text-xs font-medium" data-testid="offline-indicator" title="Connexion réseau perdue — les modifications reprendront dès le retour en ligne">
                  <WifiOff size={13} /> Hors ligne
                </div>
              )}
              <Button size="sm" onClick={() => window.open(`${baseUrl}/event/${slug}/display`, '_blank')} className="h-8 px-3 text-xs" data-testid="open-display-btn">
                <ExternalLink size={13} className="mr-1.5" /> {t('display.waitingTitle').split(' ')[0] || 'Display'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => copyToClipboard(`${baseUrl}/event/${slug}/display`)} className="h-8 px-3 text-xs" data-testid="copy-display-link">
                <Copy size={13} className="mr-1.5" /> {t('common.copyDisplayLink') || t('common.copy')}
              </Button>
              <ThemeToggle />
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 mb-6">
            <TabsList className="w-full sm:w-auto flex justify-between sm:justify-start gap-0 sm:gap-1 h-auto sm:h-9 p-1">
              <TabsTrigger value="athletes" data-testid="athletes-tab" className="flex-1 sm:flex-none px-2 sm:px-3 py-2 sm:py-1 flex flex-col sm:flex-row items-center gap-0.5 sm:gap-0">
                <Users size={18} className="sm:mr-2" />
                <span className="text-[10px] sm:text-sm">{t('tabs.athletes')}</span>
              </TabsTrigger>
              <TabsTrigger value="judges" data-testid="judges-tab" className="flex-1 sm:flex-none px-2 sm:px-3 py-2 sm:py-1 flex flex-col sm:flex-row items-center gap-0.5 sm:gap-0">
                <UserCheck size={18} className="sm:mr-2" />
                <span className="text-[10px] sm:text-sm">{t('tabs.judges')}</span>
              </TabsTrigger>
              <TabsTrigger value="tournament" data-testid="tournament-tab" className="flex-1 sm:flex-none px-2 sm:px-3 py-2 sm:py-1 flex flex-col sm:flex-row items-center gap-0.5 sm:gap-0">
                <Trophy size={18} className="sm:mr-2" />
                <span className="text-[10px] sm:text-sm">{t('tabs.tournament')}</span>
              </TabsTrigger>
              <TabsTrigger value="control" data-testid="control-tab" className="flex-1 sm:flex-none px-2 sm:px-3 py-2 sm:py-1 flex flex-col sm:flex-row items-center gap-0.5 sm:gap-0">
                <PlayCircle size={18} className="sm:mr-2" />
                <span className="text-[10px] sm:text-sm">{t('tabs.control')}</span>
              </TabsTrigger>
              <TabsTrigger value="settings" data-testid="settings-tab" className="flex-1 sm:flex-none px-2 sm:px-3 py-2 sm:py-1 flex flex-col sm:flex-row items-center gap-0.5 sm:gap-0">
                <Settings size={18} className="sm:mr-2" />
                <span className="text-[10px] sm:text-sm">{t('tabs.settings')}</span>
              </TabsTrigger>
            </TabsList>
            {/* Language Flag Selector */}
            <div className="flex items-center gap-1" data-testid="lang-flags">
              {[
                { code: 'fr', flag: '🇫🇷' },
                { code: 'en', flag: '🇬🇧' },
                { code: 'es', flag: '🇪🇸' },
                { code: 'ja', flag: '🇯🇵' }
              ].map(({ code, flag }) => (
                <button
                  key={code}
                  onClick={() => setLang(code)}
                  className={`text-xl px-1.5 py-1 rounded transition-all ${
                    lang === code ? 'bg-primary/10 ring-2 ring-primary scale-110' : 'opacity-50 hover:opacity-100'
                  }`}
                  data-testid={`lang-flag-${code}`}
                  title={t(`lang.${code}`)}
                >
                  {flag}
                </button>
              ))}
            </div>
          </div>

          <TabsContent value="athletes" data-testid="athletes-content">
            <AthletesTab athletes={athletes} loadData={loadData} openEditAthlete={openEditAthlete} uploadImage={uploadImage} />
          </TabsContent>

          <TabsContent value="judges" data-testid="judges-content">
            <JudgesTab judges={judges} loadData={loadData} openEditJudge={openEditJudge} uploadImage={uploadImage} slug={slug} setQrJudge={setQrJudge} />
          </TabsContent>
          <TabsContent value="tournament" data-testid="tournament-content">
            <TournamentTab
              tournaments={tournaments} tournament={tournament} setTournament={setTournament}
              setSelectedTournamentId={setSelectedTournamentId}
              battles={battles} athletes={athletes} judges={judges} judgesCount={judgesCount} setJudgesCount={setJudgesCount}
              showCreateForm={showCreateForm} setShowCreateForm={setShowCreateForm}
              createTournament={createTournament} deleteTournament={deleteTournament}
              tournamentName={tournamentName} setTournamentName={setTournamentName}
              tournamentType={tournamentType} setTournamentType={setTournamentType}
              tournamentFormat={tournamentFormat} setTournamentFormat={setTournamentFormat}
              numCircles3={numCircles3} setNumCircles3={setNumCircles3}
              numCircles4={numCircles4} setNumCircles4={setNumCircles4}
              selectedJudges={selectedJudges} setSelectedJudges={setSelectedJudges}
              loadBattles={loadBattles} loadOptionalBattle={loadOptionalBattle}
              optionalBattle={optionalBattle} optionalAthlete1={optionalAthlete1}
              setOptionalAthlete1={setOptionalAthlete1} optionalAthlete2={optionalAthlete2}
              setOptionalAthlete2={setOptionalAthlete2} createOptionalBattle={createOptionalBattle}
              deleteOptionalBattle={deleteOptionalBattle}
              startBattle={startBattle} resetBattle={resetBattle}
              skipBattle={skipBattle}
              startCircle={startCircle}
              setForfeitBattleId={setForfeitBattleId} setForfeitModalOpen={setForfeitModalOpen}
              loadData={loadData} tournamentCategory={tournamentCategory}
              setTournamentCategory={setTournamentCategory}
              getAvailableAthletes={getAvailableAthletes} getCategoryFilteredAthletes={getCategoryFilteredAthletes}
              getRoundLabel={getRoundLabel} updateBattleAthletes={updateBattleAthletes}
              openEditJudgesModal={openEditJudgesModal} loadCurrentBattle={loadCurrentBattle}
              currentBattle={currentBattle} currentCircle={currentCircle}
            />
          </TabsContent>

          <TabsContent value="control" data-testid="control-content" forceMount className="data-[state=inactive]:hidden">
            <ControlTab
              currentBattle={currentBattle} currentCircle={currentCircle}
              judges={judges} athletes={athletes} tournaments={tournaments} judgesCount={judgesCount}
              revealVote={revealVote} revealAllVotes={revealAllVotes}
              finishBattle={finishBattle} finishBattleAndNext={finishBattleAndNext} getNextBattle={getNextBattle}
              resetBattle={resetBattle} setForfeitBattleId={setForfeitBattleId} setForfeitModalOpen={setForfeitModalOpen}
              circleRevealVote={circleRevealVote} circleRevealAll={circleRevealAll}
              circleFinish={circleFinish} finishCircleAndNext={finishCircleAndNext}
              circleReset={circleReset} circleDeleteVote={circleDeleteVote}
              circleForfeit={circleForfeit} circleCancelForfeit={circleCancelForfeit}
              manualBattleScores={manualBattleScores} setManualBattleScores={setManualBattleScores}
              submitManagerBattleVote={submitManagerBattleVote}
              manualCircleScores={manualCircleScores} setManualCircleScores={setManualCircleScores}
              submitManagerCircleVote={submitManagerCircleVote}
              upcomingSections={upcomingSections} reorderSections={reorderSections}
              startBattle={startBattle} startCircle={startCircle} loadCurrentBattle={loadCurrentBattle} loadUpcomingEvents={loadUpcomingEvents}
              countdownMode={countdownMode}
              battleTimers={battleTimers}
              circleTimers={circleTimers}
              refreshBattleTimer={refreshBattleTimer}
              refreshCircleTimer={refreshCircleTimer}
              simulationMode={simulationMode}
              eventId={event?.event_id}
              commsSendMessage={(msg, j) => comms.sendMessage(msg, j)}
              commsHelpStates={comms.helpStates}
              commsResolveHelp={(j) => comms.resolveHelp(j)}
            />
          </TabsContent>

          <TabsContent value="settings" data-testid="settings-content">
            <SettingsTab
              theme={theme} themeEdits={themeEdits} setThemeEdits={setThemeEdits}
              updateTheme={updateTheme} resetTheme={resetTheme}
              handleBackgroundImageChange={handleBackgroundImageChange}
              deleteBackgroundImage={deleteBackgroundImage}
              autoSaveTitleSize={autoSaveTitleSize}
              simulationMode={simulationMode}
              setSimulationMode={setSimulationMode}
            />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Athlete Dialog */}
      <Dialog open={!!editingAthlete} onOpenChange={() => setEditingAthlete(null)}>
        <DialogContent className="bg-background text-foreground border-white/20">
          <DialogHeader>
            <DialogTitle className="text-foreground text-2xl">{t('athletes.editTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div>
              <label className="text-base text-muted-foreground mb-2 block">{t('athletes.name')}</label>
              <Input
                placeholder={t('athletes.name')}
                value={editAthleteData.name}
                onChange={(e) => setEditAthleteData({...editAthleteData, name: e.target.value})}
                className="bg-card border-border text-foreground text-xl h-14"
              />
            </div>
            <div>
              <label className="text-base text-muted-foreground mb-2 block">{t('athletes.country')}</label>
              <CountrySelect
                value={editAthleteData.country}
                onChange={(country) => setEditAthleteData({...editAthleteData, country})}
                placeholder={t('athletes.selectCountry')}
              />
            </div>
            <div>
              <label className="text-base text-muted-foreground mb-2 block">{t('athletes.worldRanking')}</label>
              <Input
                type="number"
                placeholder={t('athletes.worldRanking')}
                value={editAthleteData.world_ranking || ""}
                onChange={(e) => setEditAthleteData({...editAthleteData, world_ranking: e.target.value ? parseInt(e.target.value) : null})}
                className="bg-card border-border text-foreground text-xl h-14"
              />
            </div>
            <div>
              <label className="text-base text-muted-foreground mb-2 block">{t('athletes.fid')}</label>
              <Input
                placeholder={t('athletes.fidPlaceholder')}
                value={editAthleteData.fid || ""}
                onChange={(e) => setEditAthleteData({...editAthleteData, fid: e.target.value})}
                className="bg-card border-border text-foreground text-xl h-14"
              />
            </div>
            <div className="flex gap-6">
              <div>
                <label className="text-base text-muted-foreground mb-2 block">{t('athletes.gender')}</label>
                <div className="flex gap-2">
                  <button onClick={() => setEditAthleteData({...editAthleteData, gender: "Male"})} className={`px-4 py-2 rounded font-bold ${editAthleteData.gender === "Male" ? 'bg-blue-600 text-foreground' : 'bg-gray-700 text-muted-foreground'}`}>{t('common.male')}</button>
                  <button onClick={() => setEditAthleteData({...editAthleteData, gender: "Female"})} className={`px-4 py-2 rounded font-bold ${editAthleteData.gender === "Female" ? 'bg-pink-600 text-foreground' : 'bg-gray-700 text-muted-foreground'}`}>{t('common.female')}</button>
                </div>
              </div>
              <div>
                <label className="text-base text-muted-foreground mb-2 block">NextGen</label>
                <button onClick={() => setEditAthleteData({...editAthleteData, nextgen: !editAthleteData.nextgen})} className={`px-4 py-2 rounded font-bold ${editAthleteData.nextgen ? 'bg-amber-600 text-foreground' : 'bg-gray-700 text-muted-foreground'}`}>
                  {editAthleteData.nextgen ? 'NG ON' : 'NG OFF'}
                </button>
              </div>
            </div>
            <div>
              <label className="text-base text-muted-foreground mb-2 block">{t('common.photoLabel')}</label>
              <PhotoPickerButtons
                onChange={handleEditAthleteImageChange}
                onDelete={(editAthleteImagePreview || editAthleteData.photo_url) ? () => _deletePhotoFor('edit-athlete') : undefined}
                testId="edit-athlete-photo"
              />
            </div>
            {editAthleteImagePreview && (
              <div className="flex items-center gap-3">
                <img src={editAthleteImagePreview.startsWith('blob:') ? editAthleteImagePreview : getImageUrl(editAthleteImagePreview)} alt="Preview" className="w-16 h-16 rounded-full object-cover" />
                <span className="text-base text-muted-foreground">{t('common.currentImage')}</span>
                {editAthleteData.photo_url && (
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={async () => {
                      await deleteImage(editAthleteData.photo_url);
                      setEditAthleteData({...editAthleteData, photo_url: ""});
                      setEditAthleteImagePreview(null);
                      toast.success(t('common.photoDeleted'));
                    }}
                  >
                    <Trash2 size={16} /> {t('common.deletePhoto')}
                  </Button>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingAthlete(null)} className="bg-muted text-foreground border-border text-base h-11">
              {t('common.cancel')}
            </Button>
            <Button onClick={updateAthlete} className="bg-primary hover:bg-primary/90 text-base h-11">
              {t('common.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Judge Dialog */}
      <Dialog open={!!editingJudge} onOpenChange={() => setEditingJudge(null)}>
        <DialogContent className="bg-background text-foreground border-white/20">
          <DialogHeader>
            <DialogTitle className="text-foreground text-2xl">{t('judges.editTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-4">
            <div>
              <label className="text-base text-muted-foreground mb-2 block">{t('judges.judgeNumber')}</label>
              <Input
                type="number"
                placeholder={t('judges.judgeNumber')}
                value={editJudgeData.judge_number}
                onChange={(e) => setEditJudgeData({...editJudgeData, judge_number: parseInt(e.target.value)})}
                min="1"
                max="5"
                disabled
                className="bg-card border-border text-foreground text-xl h-14"
              />
            </div>
            <div>
              <label className="text-base text-muted-foreground mb-2 block">{t('judges.firstName')}</label>
              <Input
                placeholder={t('judges.firstName')}
                value={editJudgeData.first_name}
                onChange={(e) => setEditJudgeData({...editJudgeData, first_name: e.target.value})}
                className="bg-card border-border text-foreground text-xl h-14"
              />
            </div>
            <div>
              <label className="text-base text-muted-foreground mb-2 block">{t('judges.pinCode')}</label>
              <Input
                placeholder={t('judges.pinCode')}
                value={editJudgeData.pin_code}
                onChange={(e) => setEditJudgeData({...editJudgeData, pin_code: e.target.value})}
                className="bg-card border-border text-foreground text-xl h-14"
              />
            </div>
            <div>
              <label className="text-base text-muted-foreground mb-2 block">{t('common.photoLabel')}</label>
              <PhotoPickerButtons
                onChange={handleEditJudgeImageChange}
                onDelete={(editJudgeImagePreview || editJudgeData.photo_url) ? () => _deletePhotoFor('edit-judge') : undefined}
                testId="edit-judge-photo"
              />
            </div>
            {editJudgeImagePreview && (
              <div className="flex items-center gap-3">
                <img src={editJudgeImagePreview.startsWith('blob:') ? editJudgeImagePreview : getImageUrl(editJudgeImagePreview)} alt="Preview" className="w-16 h-16 rounded-full object-cover" />
                <span className="text-base text-muted-foreground">{t('common.currentImage')}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingJudge(null)} className="bg-muted text-foreground border-border text-base h-11">
              {t('common.cancel')}
            </Button>
            <Button onClick={updateJudge} className="bg-primary hover:bg-primary/90 text-base h-11">
              {t('common.saveChanges')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Forfeit Modal */}
      <Dialog open={forfeitModalOpen} onOpenChange={setForfeitModalOpen}>
        <DialogContent className="bg-background text-foreground border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl">{t('control.forfeitTitle')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-muted-foreground">{t('control.forfeitSelect')}</p>
            {forfeitBattleId && (() => {
              const battle = battles.find(b => b.id === forfeitBattleId);
              if (!battle) return null;
              const athlete1 = athletes.find(a => a.id === battle.athlete1_id);
              const athlete2 = athletes.find(a => a.id === battle.athlete2_id);
              
              return (
                <div className="space-y-3">
                  <button
                    onClick={() => setForfeitWinner(battle.athlete1_id)}
                    className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                      forfeitWinner === battle.athlete1_id
                        ? 'border-green-500 bg-green-500/20 text-foreground'
                        : 'border-border bg-muted/50 text-muted-foreground hover:border-gray-500'
                    }`}
                  >
                    <div className="text-xl font-bold">{athlete1?.name || t('tournament.athlete1')}</div>
                    <div className="text-sm text-muted-foreground">{athlete1?.country}</div>
                  </button>
                  <button
                    onClick={() => setForfeitWinner(battle.athlete2_id)}
                    className={`w-full p-4 rounded-lg border-2 transition-all text-left ${
                      forfeitWinner === battle.athlete2_id
                        ? 'border-green-500 bg-green-500/20 text-foreground'
                        : 'border-border bg-muted/50 text-muted-foreground hover:border-gray-500'
                    }`}
                  >
                    <div className="text-xl font-bold">{athlete2?.name || t('tournament.athlete2')}</div>
                    <div className="text-sm text-muted-foreground">{athlete2?.country}</div>
                  </button>
                </div>
              );
            })()}
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setForfeitModalOpen(false);
                setForfeitWinner("");
              }} 
              className="bg-muted text-foreground border-border"
            >
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={async () => {
                if (!forfeitWinner) {
                  toast.error(t('control.selectWinnerForfeit'));
                  return;
                }
                try {
                  await axios.post(`${API}/battle/${forfeitBattleId}/forfeit`, { winner_id: forfeitWinner });
                  toast.success(t('control.forfeitSuccess'));
                  setForfeitModalOpen(false);
                  setForfeitWinner("");
                  setForfeitBattleId(null);
                  if (tournament?.id) {
                    loadBattles(tournament.id);
                  }
                } catch (error) {
                  toast.error(t('common.error'));
                }
              }}
              className="bg-yellow-600 hover:bg-yellow-700"
              disabled={!forfeitWinner}
            >
              {t('control.forfeitConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Judges Modal */}
      <Dialog open={showEditJudgesModal} onOpenChange={setShowEditJudgesModal}>
        <DialogContent className="bg-card text-foreground border-gray-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl">{t('tournament.editTournamentJudges')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <label className="block text-xl font-semibold mb-3 text-foreground">{t('tournament.numberOfJudges')}:</label>
              <div className="flex gap-3">
                {[3, 5].map(count => (
                  <button
                    key={count}
                    onClick={() => {
                      setEditJudgesCount(count);
                      setEditSelectedJudges([]);
                    }}
                    className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                      editJudgesCount === count
                        ? 'border-green-500 bg-green-500/20 text-foreground'
                        : 'border-border bg-muted/50 text-muted-foreground hover:border-gray-500'
                    }`}
                  >
                    <div className="text-3xl font-bold">{t('tournament.countJudges').replace('{count}', count)}</div>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-xl font-semibold mb-3 text-foreground">
                {t('tournament.selectJudgesCount').replace('{count}', editSelectedJudges.length).replace('{total}', editJudgesCount)}
              </label>
              <div className="grid grid-cols-5 gap-3">
                {judges.map(judge => (
                  <button
                    key={judge.judge_number}
                    onClick={() => {
                      if (editSelectedJudges.includes(judge.judge_number)) {
                        setEditSelectedJudges(editSelectedJudges.filter(j => j !== judge.judge_number));
                      } else if (editSelectedJudges.length < editJudgesCount) {
                        setEditSelectedJudges([...editSelectedJudges, judge.judge_number]);
                      }
                    }}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      editSelectedJudges.includes(judge.judge_number)
                        ? 'border-blue-500 bg-blue-500/20 text-foreground'
                        : 'border-border bg-muted/50 text-muted-foreground hover:border-gray-500'
                    }`}
                  >
                    <div className="text-4xl font-bold mb-1">#{judge.judge_number}</div>
                    <div className="text-sm">{judge.first_name}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditJudgesModal(false)} className="bg-muted text-foreground border-border">
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={updateTournamentJudges}
              className="bg-primary hover:bg-primary/90"
              disabled={editSelectedJudges.length !== editJudgesCount}
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Code Modal */}
      <Dialog open={!!qrJudge} onOpenChange={() => setQrJudge(null)}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center text-foreground">
              QR Code — {qrJudge?.first_name} (#{qrJudge?.judge_number})
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-4">
            <div className="bg-white p-4 rounded-xl" data-testid="qr-code-container">
              {qrJudge && (
                <QRCodeSVG
                  value={`${baseUrl}/event/${slug}/judge/${qrJudge.judge_number}`}
                  size={220}
                  level="H"
                  includeMargin={false}
                />
              )}
            </div>
            <p className="text-xs text-muted-foreground text-center break-all px-4">
              {qrJudge && `${baseUrl}/event/${slug}/judge/${qrJudge.judge_number}`}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="text-xs border-border hover:bg-muted"
                onClick={() => {
                  copyToClipboard(`${baseUrl}/event/${slug}/judge/${qrJudge.judge_number}`);
                }}
                data-testid="qr-copy-link"
              >
                <Copy size={12} className="mr-1" /> {t('common.copy')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-xs border-border hover:bg-muted"
                onClick={() => {
                  const svg = document.querySelector('[data-testid="qr-code-container"] svg');
                  if (!svg) return;
                  const svgData = new XMLSerializer().serializeToString(svg);
                  const canvas = document.createElement('canvas');
                  canvas.width = 440; canvas.height = 440;
                  const ctx = canvas.getContext('2d');
                  ctx.fillStyle = '#ffffff';
                  ctx.fillRect(0, 0, 440, 440);
                  const img = new Image();
                  img.onload = () => {
                    ctx.drawImage(img, 0, 0, 440, 440);
                    const a = document.createElement('a');
                    a.download = `judge-${qrJudge.judge_number}-${qrJudge.first_name}.png`;
                    a.href = canvas.toDataURL('image/png');
                    a.click();
                  };
                  img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                }}
                data-testid="qr-download"
              >
                <Download size={12} className="mr-1" /> PNG
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <PhotoCropDialog
        file={pendingCrop?.file || null}
        onCancel={() => setPendingCrop(null)}
        onConfirm={handleCropConfirm}
      />
    </div>
  );
};

export default DirectorPage;